/**
 * Device Type → Breakpoint Utility
 *
 * Converts WordPress editor device type strings (e.g. "Desktop", "Tablet", "Mobile")
 * to the corresponding internal `ResponsiveBreakpoint` values used throughout the
 * Redfox Thank You control-group system.
 *
 * If an unknown string is passed, the function gracefully falls back to the
 * `desktop` breakpoint.
 *
 * @package RedfoxThankYou
 * @since 1.0.0
 */

import type { ResponsiveBreakpoint } from '../../types';

/**
 * Maps WordPress editor device preview type to a `ResponsiveBreakpoint`.
 *
 * Example:
 * ````ts
 * const breakpoint = deviceTypeToBreakpoint( 'Tablet' ); // => 'tablet'
 * ```
 */
export const deviceTypeToBreakpoint = (
	// Using `unknown` guards against accidental non-string values.
	type: unknown,
): ResponsiveBreakpoint => {
	// Normalise to string – fallback to empty string so switch default is hit.
	const normalized = String( type ?? '' );

	switch ( normalized ) {
		case 'Tablet':
		case 'tablet':
			return 'tablet';
		case 'Mobile':
		case 'mobile':
			return 'mobile';
		default:
			return 'desktop';
	}
};
